import { useRef, useEffect } from "react";
import { tickPulse, resetPulse } from "../audioAnalyser";

/**
 * usePulseLoop
 *
 * Runs the rAF animation loop that reads audio onset data and triggers
 * musically-synced shape promotion. Also toggles the `pulse-on` CSS
 * class on the container for beat-reactive brightness effects.
 */
export function usePulseLoop({
  shapeState,
  svgContainer,
  isCurrentlyPlaying,
  isActive,
  percentagePlayedRef,
  promoteShapes
}) {
  const pulseTimerRef = useRef(null);

  useEffect(() => {
    if (!isCurrentlyPlaying || !isActive) return;
    resetPulse(); // fresh baseline for each play session
    let raf;
    const loop = () => {
      const { onset } = tickPulse();

      // ── Fire-and-forget pulse: class toggle on onset ──────────────
      // CSS transitions handle attack (0.05s) and decay (0.3s).
      if (onset && svgContainer.current) {
        svgContainer.current.classList.add("pulse-on");
        clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = setTimeout(() => {
          if (svgContainer.current)
            svgContainer.current.classList.remove("pulse-on");
        }, 80);
      }

      // ── Shape + hero promotion logic ────────────────────────────────
      const state = shapeState.current;
      const hasPendingHero = state.pendingHeroId !== state.prevHeroId;
      // For actives, promoteShapes will do its own set diff — just
      // check whether the accumulator could differ from committed.
      const hasPending = hasPendingHero || state.pendingActiveIds.size > 0;

      if (hasPending) {
        if (onset) {
          promoteShapes();
        } else {
          // Shape-exit fallback: if playback has left the committed
          // hero's bounds entirely AND a new hero is waiting, promote
          // immediately. When no new shape encompasses the progress bar
          // the previous hero persists ("always a hero").
          const frac = percentagePlayedRef.current / 100;
          const { start, end } = state.currentHeroBounds;
          const outsideCurrent =
            state.prevHeroId && (frac < start || frac > end);
          if (outsideCurrent && state.pendingHeroId !== state.prevHeroId) {
            promoteShapes();
          }
        }
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const node = svgContainer.current;
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(pulseTimerRef.current);
      if (node) {
        node.classList.remove("pulse-on");
      }
    };
  }, [
    isCurrentlyPlaying,
    isActive,
    promoteShapes,
    shapeState,
    svgContainer,
    percentagePlayedRef
  ]);
}
